/**
 * Factur-X CII (Cross Industry Invoice) XML generator
 * Conforming to the EN 16931 Factur-X profile used by PDP/PPF validators.
 */

export const FACTUR_X_GUIDELINE_ID = 'urn:cen.eu:en16931:2017';
export const FACTUR_X_PROFILE = 'EN 16931';
export const FACTUR_X_XML_FILENAME = 'factur-x.xml';

export interface InvoiceXmlData {
  invoiceNumber: string;
  issueDate: string; // YYYY-MM-DD
  dueDate?: string;
  // Seller
  sellerName: string;
  sellerSiret: string;
  sellerVatNumber: string;
  sellerAddress: { street?: string; city?: string; postalCode?: string; country?: string };
  sellerEmail: string;
  // Buyer
  buyerName: string;
  buyerEmail: string;
  buyerAddress: { street?: string; city?: string; postalCode?: string; country?: string };
  // Lines
  items: { description: string; quantity: number; unitPrice: number; vatRate: number }[];
  // Totals
  totalHt: number;
  totalVat: number;
  totalTtc: number;
  // Payment
  paymentMethod?: string;
  currency?: string;
  notes?: string;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fmtDate(d: string): string {
  return d.replace(/-/g, '');
}

function fmtAmount(n: number): string {
  return n.toFixed(2);
}

function vatCategory(rate: number): string {
  return rate > 0 ? 'S' : 'Z';
}

/** Compute French intracom VAT number from a SIRET (or SIREN) string. */
export function vatFromSiret(siret: string | undefined | null): string | null {
  if (!siret) return null;
  const s = siret.replace(/\D/g, '');
  if (s.length < 9) return null;
  const siren = parseInt(s.slice(0, 9), 10);
  if (Number.isNaN(siren)) return null;
  const key = (12 + 3 * (siren % 97)) % 97;
  return `FR${String(key).padStart(2, '0')}${s.slice(0, 9)}`;
}

export function generateFacturXml(data: InvoiceXmlData): string {
  const currency = data.currency || 'EUR';

  const sellerSirenOrSiret = (data.sellerSiret || '').replace(/\D/g, '');
  const sellerVat = data.sellerVatNumber || vatFromSiret(sellerSirenOrSiret) || '';

  if (!sellerSirenOrSiret && !sellerVat) {
    throw new Error('SIRET ou numero de TVA vendeur requis pour generer la facture Factur-X.');
  }

  // Group VAT by rate
  const vatBreakdown = new Map<number, { base: number; tax: number }>();
  for (const item of data.items) {
    const rate = item.vatRate;
    const lineHt = item.quantity * item.unitPrice;
    const lineTax = (lineHt * rate) / 100;
    const existing = vatBreakdown.get(rate) || { base: 0, tax: 0 };
    existing.base += lineHt;
    existing.tax += lineTax;
    vatBreakdown.set(rate, existing);
  }

  const lineItems = data.items
    .map(
      (item, idx) => `
    <ram:IncludedSupplyChainTradeLineItem>
      <ram:AssociatedDocumentLineDocument>
        <ram:LineID>${idx + 1}</ram:LineID>
      </ram:AssociatedDocumentLineDocument>
      <ram:SpecifiedTradeProduct>
        <ram:Name>${esc(item.description)}</ram:Name>
      </ram:SpecifiedTradeProduct>
      <ram:SpecifiedLineTradeAgreement>
        <ram:NetPriceProductTradePrice>
          <ram:ChargeAmount>${fmtAmount(item.unitPrice)}</ram:ChargeAmount>
          <ram:BasisQuantity unitCode="C62">1</ram:BasisQuantity>
        </ram:NetPriceProductTradePrice>
      </ram:SpecifiedLineTradeAgreement>
      <ram:SpecifiedLineTradeDelivery>
        <ram:BilledQuantity unitCode="C62">${item.quantity}</ram:BilledQuantity>
      </ram:SpecifiedLineTradeDelivery>
      <ram:SpecifiedLineTradeSettlement>
        <ram:ApplicableTradeTax>
          <ram:TypeCode>VAT</ram:TypeCode>
          <ram:CategoryCode>${vatCategory(item.vatRate)}</ram:CategoryCode>
          <ram:RateApplicablePercent>${fmtAmount(item.vatRate)}</ram:RateApplicablePercent>
        </ram:ApplicableTradeTax>
        <ram:SpecifiedTradeSettlementLineMonetarySummation>
          <ram:LineTotalAmount>${fmtAmount(item.quantity * item.unitPrice)}</ram:LineTotalAmount>
        </ram:SpecifiedTradeSettlementLineMonetarySummation>
      </ram:SpecifiedLineTradeSettlement>
    </ram:IncludedSupplyChainTradeLineItem>`,
    )
    .join('\n');

  const vatEntries = Array.from(vatBreakdown.entries())
    .map(
      ([rate, { base, tax }]) => `
        <ram:ApplicableTradeTax>
          <ram:CalculatedAmount>${fmtAmount(tax)}</ram:CalculatedAmount>
          <ram:TypeCode>VAT</ram:TypeCode>
          <ram:BasisAmount>${fmtAmount(base)}</ram:BasisAmount>
          <ram:CategoryCode>${vatCategory(rate)}</ram:CategoryCode>
          <ram:RateApplicablePercent>${fmtAmount(rate)}</ram:RateApplicablePercent>
        </ram:ApplicableTradeTax>`,
    )
    .join('\n');

  const sellerLegalOrg = sellerSirenOrSiret
    ? `<ram:SpecifiedLegalOrganization><ram:ID schemeID="0002">${esc(sellerSirenOrSiret)}</ram:ID></ram:SpecifiedLegalOrganization>`
    : '';
  const sellerUri = sellerSirenOrSiret
    ? `<ram:URIUniversalCommunication><ram:URIID schemeID="0225">${esc(sellerSirenOrSiret)}</ram:URIID></ram:URIUniversalCommunication>`
    : '';
  const sellerTaxReg = sellerVat
    ? `<ram:SpecifiedTaxRegistration><ram:ID schemeID="VA">${esc(sellerVat)}</ram:ID></ram:SpecifiedTaxRegistration>`
    : sellerSirenOrSiret
      ? `<ram:SpecifiedTaxRegistration><ram:ID schemeID="FC">${esc(sellerSirenOrSiret)}</ram:ID></ram:SpecifiedTaxRegistration>`
      : '';

  const paymentTerms = `<ram:SpecifiedTradePaymentTerms>
          ${
            data.dueDate
              ? `<ram:DueDateDateTime><udt:DateTimeString format="102">${fmtDate(data.dueDate)}</udt:DateTimeString></ram:DueDateDateTime>`
              : `<ram:Description>Paiement a reception de facture</ram:Description>`
          }
        </ram:SpecifiedTradePaymentTerms>`;

  return `<?xml version="1.0" encoding="UTF-8"?>
<rsm:CrossIndustryInvoice xmlns:qdt="urn:un:unece:uncefact:data:standard:QualifiedDataType:100"
  xmlns:ram="urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100"
  xmlns:rsm="urn:un:unece:uncefact:data:standard:CrossIndustryInvoice:100"
  xmlns:udt="urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100">
  <rsm:ExchangedDocumentContext>
    <ram:BusinessProcessSpecifiedDocumentContextParameter>
      <ram:ID>A1</ram:ID>
    </ram:BusinessProcessSpecifiedDocumentContextParameter>
    <ram:GuidelineSpecifiedDocumentContextParameter>
      <ram:ID>${FACTUR_X_GUIDELINE_ID}</ram:ID>
    </ram:GuidelineSpecifiedDocumentContextParameter>
  </rsm:ExchangedDocumentContext>
  <rsm:ExchangedDocument>
    <ram:ID>${esc(data.invoiceNumber)}</ram:ID>
    <ram:TypeCode>380</ram:TypeCode>
    <ram:IssueDateTime>
      <udt:DateTimeString format="102">${fmtDate(data.issueDate)}</udt:DateTimeString>
    </ram:IssueDateTime>
    ${data.notes ? `<ram:IncludedNote><ram:Content>${esc(data.notes)}</ram:Content></ram:IncludedNote>` : ''}
  </rsm:ExchangedDocument>
  <rsm:SupplyChainTradeTransaction>
${lineItems}
    <ram:ApplicableHeaderTradeAgreement>
      <ram:SellerTradeParty>
        <ram:Name>${esc(data.sellerName)}</ram:Name>
        ${sellerLegalOrg}
        <ram:PostalTradeAddress>
          <ram:CountryID>${esc((data.sellerAddress.country || 'FR').toUpperCase())}</ram:CountryID>
        </ram:PostalTradeAddress>
        ${sellerUri}
        ${sellerTaxReg}
      </ram:SellerTradeParty>
      <ram:BuyerTradeParty>
        <ram:Name>${esc(data.buyerName)}</ram:Name>
        <ram:PostalTradeAddress>
          <ram:CountryID>${esc((data.buyerAddress.country || 'FR').toUpperCase())}</ram:CountryID>
        </ram:PostalTradeAddress>
      </ram:BuyerTradeParty>
    </ram:ApplicableHeaderTradeAgreement>
    <ram:ApplicableHeaderTradeDelivery>
      <ram:ActualDeliverySupplyChainEvent>
        <ram:OccurrenceDateTime>
          <udt:DateTimeString format="102">${fmtDate(data.issueDate)}</udt:DateTimeString>
        </ram:OccurrenceDateTime>
      </ram:ActualDeliverySupplyChainEvent>
    </ram:ApplicableHeaderTradeDelivery>
    <ram:ApplicableHeaderTradeSettlement>
      <ram:InvoiceCurrencyCode>${currency}</ram:InvoiceCurrencyCode>
${vatEntries}
      ${paymentTerms}
      <ram:SpecifiedTradeSettlementHeaderMonetarySummation>
        <ram:LineTotalAmount>${fmtAmount(data.totalHt)}</ram:LineTotalAmount>
        <ram:TaxBasisTotalAmount>${fmtAmount(data.totalHt)}</ram:TaxBasisTotalAmount>
        <ram:TaxTotalAmount currencyID="${currency}">${fmtAmount(data.totalVat)}</ram:TaxTotalAmount>
        <ram:GrandTotalAmount>${fmtAmount(data.totalTtc)}</ram:GrandTotalAmount>
        <ram:DuePayableAmount>${fmtAmount(data.totalTtc)}</ram:DuePayableAmount>
      </ram:SpecifiedTradeSettlementHeaderMonetarySummation>
    </ram:ApplicableHeaderTradeSettlement>
  </rsm:SupplyChainTradeTransaction>
</rsm:CrossIndustryInvoice>`;
}
